/**
 * Turning a failed download response into something a user can act on.
 *
 * The download endpoints (generate-code, generate-flattened-pdf) answer with a binary
 * body on success, so their failures travel in headers rather than in a JSON body:
 *
 * - `X-Error-Message` — human-readable text, safe to show verbatim.
 * - `X-Error-Code`    — a stable, greppable identifier for the failure kind. Support
 *                       keys on it: the backend logs the same string beside the stack
 *                       trace, so a customer quoting the code from the dialog can be
 *                       matched to the log line holding the real cause.
 *
 * Both a tier denial and a technical fault now carry a code, so the code alone no
 * longer says which kind this is. The discriminator is the STATUS: these endpoints
 * answer 403 only from their tier-enforcement branches (PDFUploadController's two
 * `!enforcement.isAllowed()` blocks); every technical failure is 400, 408 or 500.
 * That is a positive test on both signals, not an inference from a missing header.
 *
 * Reading these requires the backend to expose them via CORS in development
 * (see CorsConfig); production is same-origin behind nginx.
 */

export const SUPPORT_EMAIL = 'support@cavuinnovations.com';

/** The subset of Headers this module needs — keeps it testable without a real Response. */
export interface ReadableHeaders {
  get(name: string): string | null;
}

export interface DescribedFailure {
  /** Ready to show to the user, as-is. */
  message: string;
  /** Enforcement code when the server sent one, otherwise the caller's fallback. */
  errorCode: string;
  /** True when this was a tier-enforcement denial rather than a technical fault. */
  isLimit: boolean;
}

/** Give the sentence a full stop so following guidance does not run into it. */
function endSentence(text: string): string {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/** These endpoints answer 403 only from a tier-enforcement denial. */
const ENFORCEMENT_DENIED_STATUS = 403;

export function describeDownloadFailure(
  status: number,
  headers: ReadableHeaders,
  fallbackErrorCode: string
): DescribedFailure {
  const serverMessage = headers.get('X-Error-Message');
  const serverCode = headers.get('X-Error-Code');

  if (status === ENFORCEMENT_DENIED_STATUS && serverCode && serverMessage) {
    // A limit. The enforcement message is already self-contained — it names the tier,
    // the limit, the count consumed, the reset date and the correct contact path
    // (TierEnforcementService builds that tail itself). Adding our own contact advice
    // here would duplicate or contradict routing that was decided separately, so this
    // branch deliberately passes the message through untouched.
    return { message: serverMessage, errorCode: serverCode, isLimit: true };
  }

  if (serverMessage) {
    // A technical fault: the server explained what went wrong, and said whether the
    // user can act on it, but this is not something upgrading resolves — so route them
    // to support and keep the server's own code so Support can find the log line.
    return {
      message: `${endSentence(serverMessage)} If this keeps happening, contact ${SUPPORT_EMAIL}.`,
      errorCode: serverCode || fallbackErrorCode,
      isLimit: false,
    };
  }

  // Nothing readable came back. Never leave the user holding a bare status number:
  // say what we know and where to take it.
  return {
    message:
      `The server returned status ${status} without an error message. ` +
      `Please try again, and contact ${SUPPORT_EMAIL} if the problem persists.`,
    errorCode: fallbackErrorCode,
    isLimit: false,
  };
}
